import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  Operation,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { RetryLink } from '@apollo/client/link/retry'
import site from '@src/state/site/site'
import siteConfig from '@src/state/site/siteConfig'
import { captureError } from '../errors'
import { LOCAL_OR_PLAYGROUND } from '../localOrPlayground'
import { getJwt } from '../localStorage'
import { tokenRequest } from './cfeAuthApi'

export interface ResponseError {
  statusCode: number
  bodyText: string
  response?: any
  result?: {
    errors: ApolloServerError[]
  }
}

interface ApolloServerError {
  message: string
  extensions: {
    code: string
  }
}

const httpLink = createHttpLink({
  uri: siteConfig(site, LOCAL_OR_PLAYGROUND).bffUrl,
})

export const checkStatusCodesRefresh = (error: ResponseError) =>
  error.statusCode === 401 ||
  (error.statusCode === 400 &&
    error.result?.errors?.[0]?.extensions.code === 'UNAUTHENTICATED')

export const retry = async (error: ResponseError, $operation: Operation) => {
  captureError(new Error(`Error: ${error.bodyText}, Status: ${error.statusCode}`))
  if (checkStatusCodesRefresh(error)) {
    try {
      await tokenRequest()
      const oldContext = $operation.getContext()
      $operation.setContext({
        ...oldContext,
        headers: {
          Authorization: `Bearer ${getJwt()}`,
        },
      })
      return true
    } catch (e) {
      return false
    }
  } else if (error.statusCode >= 500) {
    return true
  }
  return false
}

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
  },
  attempts: {
    max: 3,
    retryIf: retry,
  },
})

const authLink = setContext((_, { headers }) => {
  const token = getJwt()
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  }
})

const link = ApolloLink.from([authLink, retryLink, httpLink])

const client = new ApolloClient({
  link,
  cache: new InMemoryCache(),
})

export default client



// WEBPACK FOOTER //
// ./src/utils/http-clients/graphApi.ts